import React, { useCallback, useEffect, useState } from 'react';
import { X, CheckCircle2, AlertTriangle } from 'lucide-react';
import { FONT, btnStyle } from './theme';

export default function EngineSettingsPanel({ c, onClose }) {
  const [systemInfo, setSystemInfo] = useState(null);
  const [engineStatus, setEngineStatus] = useState(null);
  const [prefs, setPrefs] = useState({});
  const [resourceMode, setResourceMode] = useState('balanced');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const [sysResult, statusResult, prefsResult, resourceResult] = await Promise.all([
        window.electronAPI.proxyQueue.getSystemInfo(),
        window.electronAPI.proxyQueue.getEngineStatus(),
        window.electronAPI.proxyQueue.getEnginePreferences(),
        window.electronAPI.proxyQueue.getResourceMode(),
      ]);
      if (sysResult?.success) setSystemInfo(sysResult.data);
      if (statusResult?.success) setEngineStatus(statusResult.data);
      if (prefsResult?.success) setPrefs(prefsResult.data);
      if (resourceResult?.success) setResourceMode(resourceResult.data?.mode || resourceResult.data);
      setLoading(false);
    })();
  }, []);

  const choosePreference = useCallback(async (engine) => {
    const result = await window.electronAPI.proxyQueue.setEnginePreference(engine);
    if (result?.success) setPrefs(result.data);
  }, []);

  const chooseResourceMode = useCallback(async (mode) => {
    const result = await window.electronAPI.proxyQueue.setResourceMode(mode);
    if (result?.success) {
      setResourceMode(result.data?.mode || mode);
      if (result.data?.capabilities) setSystemInfo(result.data.capabilities);
    }
  }, []);

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ width: 480, maxHeight: '80vh', overflow: 'auto', background: c.bg, borderRadius: '10px', border: `1px solid ${c.rule}`, padding: '18px 20px' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
          <span style={{ fontSize: '13px', fontWeight: 700 }}>Proxy Engine Settings</span>
          <button onClick={onClose} style={btnStyle(c, 'ghost')}><X size={12} /></button>
        </div>

        {loading && <div style={{ fontSize: '12px', color: c.muted }}>Loading…</div>}

        {!loading && systemInfo && (
          <div style={{ padding: '10px 12px', border: `1px solid ${c.rule}`, borderRadius: '8px', marginBottom: '14px', fontSize: '11px', background: c.subtleBg }}>
            <div style={{ fontWeight: 600, marginBottom: '4px' }}>This machine</div>
            <div style={{ color: c.muted }}>
              {systemInfo.cores} CPU cores · {systemInfo.totalRamGB} GB RAM · running {systemInfo.selectedConcurrency ?? systemInfo.recommendedConcurrency} proxy job{(systemInfo.selectedConcurrency ?? systemInfo.recommendedConcurrency) === 1 ? '' : 's'} at once
            </div>
            {!systemInfo.meetsMinimum && (
              <div style={{ color: c.warn, marginTop: '4px' }}>
                Below recommended minimum ({systemInfo.minimum.cores} cores / {systemInfo.minimum.ramGB}GB RAM) — proxy generation will work but may be slow, capped to 1 job at a time.
              </div>
            )}
          </div>
        )}

        {!loading && engineStatus && (
          <>
          <div style={{ marginBottom: '14px' }}>
            <div style={{ fontSize: '11px', fontWeight: 600, marginBottom: '6px' }}>Proxy processor for every camera format</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {['standard', 'resolve'].map((engine) => {
                const status = engine === 'resolve' ? engineStatus.resolve : { available: true };
                const selected = (prefs.engine || 'standard') === engine;
                return (
                  <button
                    key={engine}
                    onClick={() => choosePreference(engine)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px',
                      border: `1px solid ${selected ? c.accent : c.rule}`, borderRadius: '6px',
                      background: selected ? c.subtleBg : 'transparent', cursor: 'pointer', textAlign: 'left',
                      fontFamily: FONT,
                    }}
                  >
                    {status?.available
                      ? <CheckCircle2 size={13} color={c.ok} />
                      : <AlertTriangle size={13} color={c.warn} />}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '12px', fontWeight: selected ? 600 : 400 }}>{engine === 'resolve' ? 'DaVinci Resolve' : 'Standard tools'}</div>
                      {!status?.available && status?.missing?.length > 0 && (
                        <div style={{ fontSize: '10px', color: c.muted }}>Missing: {status.missing.join(', ')}</div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '11px', fontWeight: 600, marginBottom: '6px' }}>Machine resources</div>
            <div style={{ display: 'flex', gap: '6px' }}>
              {['balanced', 'maximum'].map(mode => (
                <button key={mode} onClick={() => chooseResourceMode(mode)} style={btnStyle(c, resourceMode === mode ? 'primary' : 'default')}>
                  {mode === 'maximum' ? 'Maximum (opt in)' : 'Balanced'}
                </button>
              ))}
            </div>
          </div>
          </>
        )}
      </div>
    </div>
  );
}
